import React, { useRef } from "react";
import classNames from "classnames";
import { useKeyboardCommand } from "@hooks/keyboard/useKeyboardCommand";
import { StateAwareLink } from "@components/routes/StateAwareLink";
import { HorizontalLine } from "@design-system/HorizontalLine/HorizontalLine";
import { TermsAndPrivacyConsent } from "@design-system/TermsAndPrivacyConsent/TermsAndPrivacyConsent";
import { CaptureInteractions } from "@design-system/CaptureClicks/CaptureInteractions";
import { Shortcut } from "@design-system/Shortcut/Shortcut";
import {
    GoogleLoginHandle,
    LoginWithGoogleButton,
} from "../../../auth/GoogleAuth/LoginWithGoogleButton";
import animation from "../../layout/OnboardingLayout.module.scss";
import commonStyles from "../common.module.scss";
import { RegisterWithEmail } from "./RegisterWithEmail";
import styles from "./Register.module.scss";

interface Props {
    onSubmit(email: string): Promise<void>;
    initialEmail?: string;
}

export const Register: React.FC<Props> = ({ onSubmit, initialEmail }) => {
    const googleRef = useRef<GoogleLoginHandle | null>(null);

    useKeyboardCommand({
        keys: "g",
        callback: () => {
            googleRef.current?.login();
        },
        options: {
            description: "Continue with Google",
        },
    });

    return (
        <div className={styles.contentContainer}>
            <header
                className={classNames(
                    styles.header,
                    styles.section,
                    animation.fadeIn2,
                )}
            >
                <h1 className={commonStyles.title}>
                    Accounting software that
                    <br />
                    does the work with you
                </h1>
            </header>

            <section className={classNames(styles.section, animation.fadeIn1)}>
                <LoginWithGoogleButton
                    variant="primary"
                    block
                    size="xl"
                    ref={googleRef}
                    rightIcon={
                        <span data-slot-box="">
                            <Shortcut keys="g" />
                        </span>
                    }
                >
                    Continue with Google
                </LoginWithGoogleButton>

                <HorizontalLine className={styles.spacer}>or</HorizontalLine>

                <CaptureInteractions className="d-block">
                    <RegisterWithEmail
                        onSubmit={onSubmit}
                        initialEmail={initialEmail}
                    />
                </CaptureInteractions>
            </section>

            <div className={animation.fadeIn1}>
                <TermsAndPrivacyConsent />

                <footer className="mt-5 text-center">
                    Already have an account?{" "}
                    <StateAwareLink to="/">Log in</StateAwareLink>
                </footer>
            </div>
        </div>
    );
};
